import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppShell } from '../components/Layout';
import { doctorAPI } from '../services/api';
import Icon from '../components/Icon';

const EMPTY_FORM = {
  firstName: '', lastName: '', email: '', phone: '',
  specialty: '', licenseNumber: '', isAvailable: true, isOnCall: false,
};

const SPECIALTIES = [
  'Cardiology', 'Dermatology', 'Endocrinology', 'Gastroenterology',
  'General Medicine', 'Nephrology', 'Neurology', 'Oncology',
  'Orthopaedics', 'Paediatrics', 'Psychiatry', 'Pulmonology', 'Other',
];

export default function DoctorsPage() {
  const navigate = useNavigate();
  const [doctors,    setDoctors]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [showForm,   setShowForm]   = useState(false);
  const [editId,     setEditId]     = useState(null);
  const [form,       setForm]       = useState(EMPTY_FORM);
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState('');
  const [success,    setSuccess]    = useState('');
  const [tempPassword, setTempPassword] = useState('');
  const [search,     setSearch]     = useState('');
  const [filter,     setFilter]     = useState('ALL');
  const [menuId,     setMenuId]     = useState(null);
  const menuRef = useRef(null);

  const load = () => {
    setLoading(true);
    doctorAPI.list()
      .then(r => setDoctors(Array.isArray(r.data) ? r.data : []))
      .catch(() => setDoctors([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (menuId == null) return undefined;
    const onDown = e => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuId(null); };
    const onKey  = e => { if (e.key === 'Escape') setMenuId(null); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuId]);

  useEffect(() => {
    if (!showForm) return undefined;
    const onKey = e => { if (e.key === 'Escape' && !saving) handleCancel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showForm, saving]);

  const set = k => e => setForm(f => ({
    ...f,
    [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
  }));

  const openAdd = () => {
    setEditId(null);
    setForm(EMPTY_FORM);
    setError('');
    setSuccess('');
    setShowForm(true);
  };

  const openEdit = (doc) => {
    setEditId(doc.id);
    setForm({
      firstName:     doc.firstName || '',
      lastName:      doc.lastName  || '',
      email:         doc.email     || '',
      phone:         doc.phone     || '',
      specialty:     doc.specialty || doc.specialisation || '',
      licenseNumber: doc.licenseNumber || '',
      isAvailable:   doc.isAvailable !== false,
      isOnCall:      doc.isOnCall === true,
    });
    setError('');
    setSuccess('');
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditId(null);
    setForm(EMPTY_FORM);
    setError('');
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setSaving(true); setError(''); setSuccess(''); setTempPassword('');
    try {
      if (editId) {
        await doctorAPI.update(editId, {
          firstName:     form.firstName,
          lastName:      form.lastName,
          phone:         form.phone,
          specialty:     form.specialty,
          licenseNumber: form.licenseNumber,
          isAvailable:   form.isAvailable,
          isOnCall:      form.isOnCall,
        });
        setSuccess('Doctor updated successfully!');
      } else {
        const res = await doctorAPI.create(form);
        // The backend generates a one-time login password for the doctor's
        // auto-created account and returns it exactly once, here — there is no
        // automated credential delivery for staff accounts, so the admin has to
        // relay it. Held separately from the success banner so it can be shown
        // as something to copy rather than a message that scrolls away.
        setTempPassword(res.data?.temporaryPassword || '');
        setSuccess(res.data?.temporaryPassword
          ? 'Doctor added. Share the temporary password below — it is shown only once.'
          : 'Doctor added successfully!');
      }
      setShowForm(false);
      setEditId(null);
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      setError(err.response?.data?.message || JSON.stringify(err.response?.data?.errors) || 'Failed to save doctor.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Remove Dr. ${name}?`)) return;
    try {
      await doctorAPI.delete(id);
      setDoctors(prev => prev.filter(d => d.id !== id));
      if (editId === id) handleCancel();
    } catch { setError('Failed to delete doctor.'); }
  };

  const handleToggleAvailable = async (doc) => {
    try {
      await doctorAPI.update(doc.id, { isAvailable: !doc.isAvailable });
      setDoctors(prev => prev.map(d => d.id === doc.id ? { ...d, isAvailable: !d.isAvailable } : d));
    } catch { setError('Failed to update availability.'); }
  };

  // On call = also sees critical escalations for patients who have no doctor.
  // Several doctors can be on call at once; it is off unless turned on here.
  const handleToggleOnCall = async (doc) => {
    const next = !doc.isOnCall;
    try {
      await doctorAPI.update(doc.id, { isOnCall: next });
      setDoctors(prev => prev.map(d => d.id === doc.id ? { ...d, isOnCall: next } : d));
    } catch { setError('Failed to update on-call status.'); }
  };

  const filtered = doctors.filter(d => {
    const matchSearch = !search ||
      `${d.firstName} ${d.lastName}`.toLowerCase().includes(search.toLowerCase()) ||
      (d.specialty || '').toLowerCase().includes(search.toLowerCase()) ||
      (d.email || '').toLowerCase().includes(search.toLowerCase());
    const matchFilter =
      filter === 'ALL' ? true :
      filter === 'AVAILABLE' ? d.isAvailable :
      filter === 'ON_CALL' ? d.isOnCall :
      !d.isAvailable;
    return matchSearch && matchFilter;
  });

  const initials = d => `${d.firstName?.[0] || ''}${d.lastName?.[0] || ''}`.toUpperCase();
  const avatarTint = d => `avatar--t${((`${d.firstName}${d.lastName}`.length) % 5) + 1}`;
  const FILTERS = [['ALL', 'All'], ['AVAILABLE', 'Available'], ['ON_CALL', 'On call'], ['UNAVAILABLE', 'Unavailable']];

  const availableCount   = doctors.filter(d => d.isAvailable).length;
  const onCallCount      = doctors.filter(d => d.isOnCall).length;
  const unavailableCount = doctors.filter(d => !d.isAvailable).length;

  return (
    <AppShell breadcrumbs={[{ label: 'Doctors' }]}>
      <div className="page-head fade-in-up">
        <div className="page-head__text">
          <div className="page-head__eyebrow">Clinical team</div>
          <h1 className="page-head__title">Doctors</h1>
          <p className="page-head__sub">Manage your clinical team, availability and on-call coverage.</p>
        </div>
        <div className="page-head__actions">
          <button className="btn btn-primary" onClick={openAdd}>
            <Icon name="plus" size={14} /> Add doctor
          </button>
        </div>
      </div>

      <div className="stats-row fade-in-up delay-1">
        <div className="stat-card stat-card--accent">
          <div className="stat-icon stat-icon--green"><Icon name="hospital" size={20} /></div>
          <div>
            <div className="stat-value">{doctors.length}</div>
            <div className="stat-label">Total doctors</div>
          </div>
        </div>
        <div className="stat-card stat-card--accent">
          <span className="stat-bar stat-bar--green" aria-hidden="true" />
          <div>
            <div className="stat-value">{availableCount}</div>
            <div className="stat-label">Available now</div>
          </div>
        </div>
        <div className="stat-card stat-card--accent">
          <span className="stat-bar stat-bar--amber" aria-hidden="true" />
          <div>
            <div className="stat-value">{onCallCount}</div>
            <div className="stat-label">On call</div>
          </div>
        </div>
        <div className="stat-card stat-card--accent">
          <span className="stat-bar stat-bar--red" aria-hidden="true" />
          <div>
            <div className="stat-value">{unavailableCount}</div>
            <div className="stat-label">Unavailable</div>
          </div>
        </div>
      </div>

      {error   && <div className="alert alert-error"><Icon name="alert" size={14} /> {error}</div>}
      {success && <div className="alert alert-success"><Icon name="check" size={14} /> {success}</div>}
      {tempPassword && (
        <div className="alert alert-warning">
          <div className="grow">
            <div className="alert-title">Temporary password</div>
            <code>{tempPassword}</code>
            <div className="form-hint">
              Give this to the doctor directly. They must change it the first time they sign in.
              It cannot be retrieved again — reset the password from Users if it is lost.
            </div>
          </div>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => navigator.clipboard?.writeText(tempPassword)}
          >
            Copy
          </button>
        </div>
      )}

      <div className="section-head fade-in-up delay-2">
        <div>
          <div className="section-title">Team directory</div>
          <div className="section-sub">
            {loading ? 'Loading…' : `${filtered.length} ${filtered.length === 1 ? 'doctor' : 'doctors'} shown`}
          </div>
        </div>
        <div className="toolbar">
          <div className="search-input-wrap">
            <span className="search-icon"><Icon name="search" size={15} /></span>
            <input className="search-input" placeholder="Search by name or specialty"
              aria-label="Search doctors"
              value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <div className="tabs-bar" role="group" aria-label="Filter doctors by status">
            {FILTERS.map(([key, label]) => (
              <button key={key} type="button"
                className={`tab-btn${filter === key ? ' active' : ''}`}
                aria-pressed={filter === key}
                onClick={() => setFilter(key)}>
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading
        ? <div className="loading-state"><div className="spinner" />Loading doctors…</div>
        : filtered.length === 0
          ? <div className="empty-state fade-in-up">
              <div className="empty-icon empty-icon--badge" aria-hidden="true"><Icon name="hospital" size={20} /></div>
              <div className="empty-title">{search || filter !== 'ALL' ? 'No doctors found' : 'No doctors yet'}</div>
              <div className="empty-desc">
                {search || filter !== 'ALL' ? 'Try a different search or filter' : 'Add your first doctor to get started'}
              </div>
              {!search && filter === 'ALL' && (
                <button className="btn btn-primary" onClick={openAdd}>
                  <Icon name="plus" size={14} /> Add doctor
                </button>
              )}
            </div>
          : <div className="directory-grid fade-in-up delay-3">
              {filtered.map(doc => {
                const name = `${doc.firstName} ${doc.lastName}`;
                return (
                  <div key={doc.id} className="card directory-card">
                    <div className="directory-card__head">
                      <div className={`avatar ${avatarTint(doc)}`} aria-hidden="true">{initials(doc)}</div>
                      <div className="directory-card__who">
                        <div className="directory-card__name">Dr. {name}</div>
                        <div className="directory-card__meta">{doc.specialty || doc.specialisation || 'General'}</div>
                      </div>
                      <div className="status-stack">
                        <span className={`badge badge-dot ${doc.isAvailable ? 'badge-active' : 'badge-inactive'}`}>
                          {doc.isAvailable ? 'Available' : 'Unavailable'}
                        </span>
                        {doc.isOnCall && <span className="badge badge-dot badge-amber">On call</span>}
                      </div>
                    </div>

                    <div className="directory-card__details">
                      <div className="detail-row">
                        <span className="detail-row__label">Email</span>
                        <span className="detail-row__value" title={doc.email || undefined}>{doc.email || '—'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-row__label">Phone</span>
                        <span className="detail-row__value">{doc.phone || '—'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-row__label">License</span>
                        <span className="detail-row__value mono">{doc.licenseNumber || '—'}</span>
                      </div>
                    </div>

                    <div className="directory-card__foot">
                      <button className="btn btn-secondary btn-sm" onClick={() => handleToggleAvailable(doc)}>
                        {doc.isAvailable ? 'Mark unavailable' : 'Mark available'}
                      </button>
                      <button
                        className="btn btn-ghost btn-sm"
                        title="On-call doctors also see critical escalations for patients who have no doctor"
                        onClick={() => handleToggleOnCall(doc)}>
                        {doc.isOnCall ? 'Remove from on call' : 'Set on call'}
                      </button>
                      <span className="grow" />
                      <div className="menu-wrap" ref={menuId === doc.id ? menuRef : null}>
                        <button
                          className="btn btn-icon btn-sm"
                          aria-label={`More actions for Dr. ${name}`}
                          aria-haspopup="menu"
                          aria-expanded={menuId === doc.id}
                          onClick={() => setMenuId(menuId === doc.id ? null : doc.id)}>
                          ···
                        </button>
                        {menuId === doc.id && (
                          <div className="menu" role="menu">
                            <button className="menu-item" role="menuitem"
                              onClick={() => { setMenuId(null); openEdit(doc); }}>
                              Edit details
                            </button>
                            <button className="menu-item" role="menuitem"
                              onClick={() => navigate(`/doctors/${doc.id}/availability`)}>
                              Manage availability
                            </button>
                            <button className="menu-item menu-item--danger" role="menuitem"
                              onClick={() => { setMenuId(null); handleDelete(doc.id, name); }}>
                              Delete doctor
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
      }

      {showForm && (
        <div className="dialog-backdrop" onClick={() => { if (!saving) handleCancel(); }}>
          <div className="dialog dialog--form" role="dialog" aria-modal="true"
            aria-labelledby="doctor-dialog-title" onClick={e => e.stopPropagation()}>
            <div className="dialog__head">
              <div>
                <div className="dialog__eyebrow">Team management</div>
                <div className="dialog-title" id="doctor-dialog-title">
                  {editId ? 'Edit doctor' : 'Add a new doctor'}
                </div>
                <div className="dialog-sub">
                  {editId ? 'Update this doctor’s profile and availability.'
                          : 'Create a profile and set their initial availability.'}
                </div>
              </div>
              <button type="button" className="picker-close" aria-label="Close" onClick={handleCancel}>✕</button>
            </div>

            {error && <div className="alert alert-error"><Icon name="alert" size={14} /> {error}</div>}

            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label" htmlFor="doc-first">First name <span className="required">*</span></label>
                  <input id="doc-first" className="form-input" value={form.firstName} onChange={set('firstName')}
                    placeholder="e.g. Anand" required autoFocus />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="doc-last">Last name <span className="required">*</span></label>
                  <input id="doc-last" className="form-input" value={form.lastName} onChange={set('lastName')}
                    placeholder="e.g. Mehta" required />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="doc-email">Email address <span className="required">*</span></label>
                  <input id="doc-email" className={`form-input${editId ? ' readonly' : ''}`} type="email"
                    value={form.email} onChange={set('email')}
                    placeholder="doctor@hospital.com" required disabled={!!editId} />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="doc-phone">Phone number <span className="required">*</span></label>
                  <input id="doc-phone" className="form-input" type="tel" value={form.phone} onChange={set('phone')}
                    placeholder="+91 90000 00000" required />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="doc-specialty">Specialty <span className="required">*</span></label>
                  <select id="doc-specialty" className="form-select" value={form.specialty} onChange={set('specialty')} required>
                    <option value="">Select specialty</option>
                    {SPECIALTIES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="doc-license">License number</label>
                  <input id="doc-license" className="form-input" value={form.licenseNumber} onChange={set('licenseNumber')}
                    placeholder="Medical license number" />
                </div>
                <label className="form-check form-full">
                  <input type="checkbox" checked={form.isAvailable} onChange={set('isAvailable')} />
                  Currently available for appointments
                </label>
                <label className="form-check form-full">
                  <input type="checkbox" checked={form.isOnCall} onChange={set('isOnCall')} />
                  On call for critical escalations (also sees patients who have no doctor)
                </label>
              </div>
              <div className="form-actions">
                <button className="btn btn-ghost" type="button" onClick={handleCancel}>Cancel</button>
                <button className="btn btn-primary" type="submit" disabled={saving}>
                  {saving ? 'Saving…' : editId
                    ? 'Save changes'
                    : <><Icon name="plus" size={14} /> Add doctor</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AppShell>
  );
}
